import { Component, OnInit, OnDestroy, inject, ChangeDetectorRef, NgZone, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { MetaService } from '../../../core/services/meta.service';
import { WebAppConfigService } from '../../../core/services/webapp-config.service';
import { API_CONFIG } from '../../../core/config/api.config';

interface PasarelaPago {
  eventometodopagoid: string;
  nombre: string;
  comision: number;
  pasarelaData: any;
}

interface PayphoneCartSnapshot {
  cartid: string;
  userid: string | null;
  eventoid: string;
  estado: string;
  evento?: {
    url_slug?: string;
  };
  pasarela_pago: {
    eventometodopagoid: string;
    nombre: string;
    comision: number;
    pasarelaData: any;
  };
}

@Component({
  selector: 'app-payphone-cancel',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './payphone-cancel.html',
  styleUrls: ['./payphone-cancel.css']
})
export class PayphoneCancelComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private http = inject(HttpClient);
  private cdr = inject(ChangeDetectorRef);
  private ngZone = inject(NgZone);
  private platformId = inject(PLATFORM_ID);
  private metaService = inject(MetaService);
  private webAppConfig = inject(WebAppConfigService);
  private isBrowser: boolean;

  clientTransactionId: string = '';
  isLoading: boolean = false;
  error: string | null = null;
  success: boolean = false;
  eventSlug: string | null = null;
  private currentCart: PayphoneCartSnapshot | null = null;

  constructor() {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnDestroy(): void {}

  ngOnInit(): void {
    const config = this.webAppConfig.config();
    if (config) {
      this.metaService.resetToDefault(
        this.webAppConfig.getSiteName() + ' - ' + config.short_description,
        config.description,
        config.url_logo,
        config.url
      );
    }

    if (!this.isBrowser) {
      console.log('⚠️ PayphoneCancel ejecutándose en el servidor (SSR), esperando navegador...');
      return;
    }

    this.route.queryParams.subscribe(params => {
      console.log('🚫 Cancelación de pago Payphone - Query Params:', params);
      this.clientTransactionId = params['clientTransactionId'] || '';

      if (!this.clientTransactionId) {
        this.error = 'Faltan parámetros en la URL';
        console.error('❌ Falta clientTransactionId:', params);
        return;
      }

      this.validateAndExpireCart();
    });
  }

  private validateAndExpireCart(): void {
    this.isLoading = true;
    this.error = null;

    const cartid = this.clientTransactionId;
    const endpoint = API_CONFIG.endpoints.carrito.getById.replace('{cartid}', cartid);

    this.http.get<any>(`${API_CONFIG.baseUrl}${endpoint}`, {
      headers: {
        'x-api-key': API_CONFIG.api_key
      },
      withCredentials: API_CONFIG.cookie_http_only
    }).subscribe({
      next: (response) => {
        this.ngZone.run(() => {
          const cart = response?.data;
          if (!response?.success || !cart) {
            this.error = 'No se encontró el carrito para cancelar';
            this.isLoading = false;
            this.cdr.detectChanges();
            return;
          }

          this.eventSlug = cart.evento?.url_slug || null;

          if (cart.cartid !== this.clientTransactionId) {
            this.error = 'El ID de transacción no coincide con el carrito';
            this.isLoading = false;
            this.cdr.detectChanges();
            return;
          }

          if (cart.estado === 'completado') {
            this.error = 'Este pago ya fue procesado. No se puede cancelar.';
            this.isLoading = false;
            this.cdr.detectChanges();
            return;
          }

          if (cart.estado === 'expirado') {
            this.success = true;
            this.isLoading = false;
            this.cdr.detectChanges();
            return;
          }

          if (cart.estado !== 'pagando' && cart.estado !== 'activo') {
            this.error = `El carrito no está en estado válido para cancelar: ${cart.estado}`;
            this.isLoading = false;
            this.cdr.detectChanges();
            return;
          }

          this.currentCart = cart;
          this.expireCart();
        });
      },
      error: (error) => {
        this.ngZone.run(() => {
          console.error('❌ Error al validar carrito para cancelación:', error);
          this.error = 'No se pudo validar el estado del carrito';
          this.isLoading = false;
          this.cdr.detectChanges();
        });
      }
    });
  }

  private expireCart(): void {
    if (!this.currentCart) {
      this.error = 'Error: Datos del carrito no disponibles';
      this.isLoading = false;
      return;
    }

    const pasarelaPago: PasarelaPago = {
      eventometodopagoid: this.currentCart.pasarela_pago?.eventometodopagoid || '',
      nombre: 'PAYPHONE',
      comision: 0,
      pasarelaData: {
        reason: 'cancelled_by_user',
        message: 'El usuario canceló el pago desde la pasarela PayPhone',
        cancelledAt: new Date().toISOString()
      }
    };

    const requestBody = {
      cartid: this.currentCart.cartid,
      pasarela_pago: pasarelaPago
    };

    console.log('📡 Expirando carrito por cancelación:', requestBody);

    this.http.post(
      `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.carrito.paymentFailure}`,
      requestBody,
      {
        headers: {
          'x-api-key': API_CONFIG.api_key,
          'Content-Type': 'application/json'
        }
      }
    ).subscribe({
      next: (response) => {
        this.ngZone.run(() => {
          console.log('✅ Carrito expirado por cancelación:', response);
          this.success = true;
          this.isLoading = false;
          this.cdr.detectChanges();
        });
      },
      error: (error) => {
        this.ngZone.run(() => {
          console.error('❌ Error al expirar carrito:', error);
          this.error = 'Error al procesar la cancelación. Por favor, contacte con soporte.';
          this.isLoading = false;
          this.cdr.detectChanges();
        });
      }
    });
  }

  goHome(): void {
    this.router.navigate(['/']);
  }

  goBackToEvent(): void {
    if (this.eventSlug) {
      this.router.navigate(['/evento', this.eventSlug]);
    } else {
      this.router.navigate(['/']);
    }
  }
}
